<script lang="ts" setup>

import { type Address } from 'viem';

import { ref, storeToRefs, useAppConfig, useUserStore } from '#imports';
import { useReceivedOfferColumns } from '~/components/features/profile/ProfileReceivedOffers/useReceivedOfferColumns';
import type { ApiPagination, EvmFullFillCreateOrderListing, OfferListParams } from '~/service/api/types';

const props = defineProps<{
    owner: Address;
}>();

const { offer, QueryEvmKey } = useEVMApi();

type RouteStateType = OfferListParams &
    Pick<ApiPagination, 'page' | 'size'>;

const {
    network,
    user
} = storeToRefs(useUserStore());

const acceptModalData = ref<Pick<EvmFullFillCreateOrderListing, 'order' | 'price' | 'source' | 'nftInfo' | 'orderHash' | 'tokensRemaining'>>();
const isVisibleAcceptModal = ref(false);

const params = ref<RouteStateType>({
    page: 1,
    size: 4,
    direction: 'asc',
    sort: 'endTime',
    network: network.value
        ? network.value
        : 'eth'
});

const total = ref(0);

const {
    data: offers,
    status: statusOffers,
    refresh
} = useAsyncData(`${props.owner}-${QueryEvmKey.OFFER_RECEIVE_LIST}`, async () => {
    if (props.owner) {
        const { data, pagination } = await offer.listOffer({
            ...params.value,
            ownerAddress: props.owner
        });
        total.value = pagination.total;
        return data;
    }
    return [] as EvmFullFillCreateOrderListing[];
}, {
    default: () => [],
    watch: [params]
});

const isOpenFilter = ref(false);

const onChangeQueryParams = (values: Partial<RouteStateType>) => {
    params.value = { ...params.value, ...values };
};

const onChangeSort = (sort: RouteStateType['sort']) => {
    if (params.value.direction === 'asc' && params.value.sort === sort) {
        params.value = { ...params.value, direction: 'desc' };
    }
    else {
        params.value = { ...params.value, sort, direction: 'asc' };
    }
};

const {
    filter: {
        networks: filterNetworksList
    }
} = useAppConfig();

const onShowAcceptModal = (data: Pick<EvmFullFillCreateOrderListing, 'order' | 'price' | 'source' | 'nftInfo' | 'orderHash' | 'tokensRemaining'>) => {
    acceptModalData.value = data;
    isVisibleAcceptModal.value = true;
};

const owner = computed(() => props.owner === user.value ? props.owner : undefined);

const columns = computed(() =>
    useReceivedOfferColumns({
        user: owner.value,
        onShowModal: data => onShowAcceptModal(data),
        onSort: field => onChangeSort(field as RouteStateType['sort'])
    })
);

</script>

<template>
  <div class="display-flex justify-start width-full gap-16">
    <FiltersPanel v-model:model-value="isOpenFilter">
      <div class="display-flex blocks flex-column pad-24 gap-12">
        <p class="compact-500-accent">
          Blockchain
        </p>

        <ToggleIconGroup :items="filterNetworksList"
                         :model-value="params.network"
                         @update:model-value="(payload) => onChangeQueryParams({ network: payload, page: 1, size: 10 })"
        />
      </div>
    </FiltersPanel>

    <div class="display-flex flex-column grow">
      <Table :columns="columns"
             :key="owner"
             :data="offers"
             :is-loading="statusOffers === 'pending'"
             :skeleton="{ rows: 4, height: 18 }"
             class="grow table">
        <template #no-data>
          <div class="display-flex flex-column align-items-center justify-center gap-12 pad-center">
            <div class="empty-circle display-flex align-items-center justify-center">
              <IconMain :size="28"
                        icon="air"
              />
            </div>
            <div class="display-flex flex-column align-items-center justify-center text-color-primary">
              <p class="compact-500-accent">
                No orders
              </p>
            </div>
          </div>
        </template>
      </Table>
      <Suspense>
        <LazyPagination :page="params.page"
                        :size="params.size"
                        :total="total"
                        :visible-items="3"
                        @on-page="(page: number) => onChangeQueryParams({ page })"
        />
        <template #fallback>
          <div class="display-flex flex-row align-items-center paginate gap-8">
            <LoaderSkeleton :height="30"
                            :width="30" />
            <LoaderSkeleton :height="30"
                            :width="30" />
            <LoaderSkeleton :height="30"
                            :width="30" />
          </div>
        </template>
      </Suspense>
    </div>
    <ClientOnly>
      <Teleport to="body">
        <Modal v-if="acceptModalData"
               v-model:model-value="isVisibleAcceptModal"
        >
          <ModalAcceptOffer v-model:model-value="isVisibleAcceptModal"
                            v-bind="acceptModalData" />
        </Modal>
      </Teleport>
    </ClientOnly>
  </div>

</template>

<style lang="scss" scoped>
:deep(.logo) {
  width: 24px;
  height: 24px;
}
</style>
